/**
 * The SEQUENCE canvas, view-shaped: the cycle as a row of numbered columns.
 *
 * `entities/plan-draft`'s `tapeProjection` answers what the cycle IS — its days in order and the
 * break each one prescribes. This module answers what the canvas SAYS.
 *
 * **The specimen is `Builder planu sekwencja.dc.html` (07.09), and it replaced the 04.08 tape.** The
 * old canvas was a horizontal TAPE: fixed 196px tiles, a vertical "ODPOCZYNEK" strip, a loop mark
 * drawn sideways, and a visible "TAŚMA CYKLU" band over it. The mock draws the same cycle in the
 * week programator's language instead — `DZIEŃ 1 … DZIEŃ n` column heads, dashed gap columns with a
 * −/+ under them, a static loop tile closing the run, and a "Wzór powtarzalnego cyklu" row above.
 * The names in this slice stay `tape*`: the VISIBLE identity changed, not the module, and renaming
 * the tree in the same commit would mix two diffs and spill into `entities/`, the tests and the e2e
 * specs.
 *
 * Two of the old canon's captions survive as decisions rather than as description:
 *
 * · **"Kafel bez zmian"** — a training column draws the weekly card, unchanged, in a box the column
 *   owns. So it is built by the same `toDayCardView` and the only thing this module supplies that
 *   the grid does not is a POSITION where the grid supplies a weekday.
 * · **A gap column is an equal element of the row**, not a decoration between two cards. It carries
 *   its own head, its own value and its own stepper, and it is an item of the same list.
 *
 * ── The cycle's length in days is DRAWN here, and that is R2 as amended 2026-09-13 ───────────
 * The mock prints "cykl 4 dni" in the pattern row, numbers its columns straight through the gaps,
 * and normalises the volume rail to seven days by the same figure. `model/cycleDays.ts` is the one
 * place that sum is computed and its header carries the argument in full. Nothing here forwards the
 * number to anything that could schedule from it.
 *
 * **A component below this reads only the view model.** No `t`, no `Intl`, no arithmetic.
 */

import type {
  MuscleLoadBreakdown,
  PhaseSummary,
  PlanDraft,
  TapeProjection,
} from '~/entities/plan-draft';
import { REST_DAYS_AFTER_RANGE } from '~/entities/plan-draft';

import { KEY, type Translate } from './builderCopy';
import { cycleDayNumbering } from './cycleDays';
import { positionTargets } from './tapeMove';
import {
  dayDisplayName,
  toDayCardView,
  type DayCardView,
  type LookUpExercise,
} from './dayCardView';
import {
  toBuilderChrome,
  type BuilderChromeView,
  type DayMoveTargetView,
  type PatternBarView,
} from './builderChrome';

/** The break a training prescribes after itself, and the two buttons that change it. */
export interface RestColumnView {
  readonly key: string;
  /** The day the note belongs to — the address `setRestDaysAfter` writes against. */
  readonly daySlotToken: string;
  /** "Przerwa" — the mock's own word, and the trainer's word on both platforms since 13.09.2026. */
  readonly label: string;
  /**
   * "2 dni", or "bez przerwy" at zero.
   *
   * ONE string rather than a count and a noun drawn as two lines. The 04.08 canon stacked them; the
   * mock prints one line, and a zero gap prints words rather than a figure — which is also why a
   * counted plural cannot carry it (Polish routes 0 to `_many`, so a plural form would have had to
   * read "0 dni" and contradict the tile).
   */
  readonly value: string;
  /** What the whole column announces, so the gap reaches a screen reader as one sentence. */
  readonly description: string;
  /**
   * The trainer prescribed no break after this training.
   *
   * The column is still drawn (see `toTapeViewModel`), so the state needs a mark of its own or a
   * dashed panel would read as a gap that failed to render. It is a FLAG rather than a second set
   * of strings because what changes is the emphasis and the width, not the words.
   */
  readonly isZero: boolean;
  /**
   * Why neither button can act right now, or `null` — the ONE sentence the column shows and both
   * buttons are described by.
   *
   * One field rather than two, because at most one refusal can ever be in force: the bounds are 0
   * and 30, so a column cannot sit on both, and a frozen phase closes both buttons with the same
   * sentence.
   */
  readonly reason: string | null;
  readonly decrease: RestStepView;
  readonly increase: RestStepView;
}

/** One −/+ cell. Disabled at a bound is `aria-disabled` plus a reason, never the attribute. */
export interface RestStepView {
  /**
   * The value the press would write — the current gap plus or minus one, unclamped.
   *
   * It goes out of range at a bound by one (`-1` below zero, `31` above thirty) and that is
   * harmless in both directions, because the press is refused before it is read: the step carries
   * `disabledReason` and the view refuses it, and `draftOperations.setRestDaysAfter` clamps again on
   * the other side of the seam. Stated because the field used to claim it was already clamped, and
   * a reader who believed that would drop one of those two guards.
   */
  readonly value: number;
  readonly label: string;
  /**
   * Why the press would do nothing, or `null`.
   *
   * Two causes, and the second is the one that was missing: the note is already at its bound (0 or
   * 30, the backend's own `Range` — the mock clamps at 6 and that is a prototype artefact, not the
   * contract), or the PHASE is frozen because the client has trained it. A rest change is
   * `restDaysChange`, a structural edit the grips on the same cards already refuse for; the steppers
   * did not, so ± happily rewrote a phase whose cards could not be dragged.
   */
  readonly disabledReason: string | null;
}

/**
 * One column of the canvas.
 *
 * `head` and `sub` belong to the COLUMN rather than to what stands in it, exactly as they do on the
 * week: the mock draws the same two centred lines over a training, over a gap and (empty) over each
 * tail tile, so the pane draws them once and branches only on what goes underneath.
 */
export type CycleColumnView =
  | {
      readonly kind: 'day';
      readonly key: string;
      /** "Dzień 3" — uppercased by CSS, never shouted in the locale. */
      readonly head: string;
      /** "~64 min", or `null` when the trainer authored no estimate. */
      readonly sub: string | null;
      readonly day: DayCardView;
    }
  | {
      readonly kind: 'rest';
      readonly key: string;
      /** "Dzień 2" / "Dni 3–4", or `null` at a gap of zero, which consumes no day number. */
      readonly head: string | null;
      readonly rest: RestColumnView;
    };

/** The dashed tile that appends a training. Always rendered; `disabledReason` is what refuses it. */
export interface TapeAddView {
  /** "Dodaj dzień treningowy" — the accessible name, which CONTAINS the drawn words (SC 2.5.3). */
  readonly label: string;
  /** "Dzień treningowy" — the two lines the tile actually prints. */
  readonly tileLabel: string;
  /**
   * Why a training cannot be added here, or `null`. One cause is left: the phase is at the
   * fourteen-day cap. The second — a running fork's non-last phase — went with the rest-day
   * arithmetic that justified it (R2, R18); see `structuralEditFlow`'s header.
   */
  readonly disabledReason: string | null;
}

/**
 * The static tile that closes the run.
 *
 * It is NOT a control. The mock draws a plain `--bg-inset` panel with a solid edge and no hover, and
 * a decorative tile that looks pressable is a defect of its own — so there is no handler, no
 * `role="button"` and no tab stop. Its two drawn lines are `aria-hidden` and `description` is the
 * one sentence assistive technology gets, because reading the lines as they are drawn produces the
 * ungrammatical run "Cykl zapętla się wraca do dnia 1".
 */
export interface CycleLoopView {
  /** "Cykl zapętla się" — drawn over two lines by CSS. */
  readonly title: string;
  /** "wraca do dnia 1". */
  readonly hint: string;
  /** "Po ostatnim treningu cykl zapętla się i wraca do dnia 1." */
  readonly description: string;
}

export interface TapeViewModel {
  readonly chrome: BuilderChromeView;
  /**
   * "Wzorzec cyklu" — the panel's heading, VISUALLY HIDDEN.
   *
   * It was the visible "TAŚMA CYKLU" band the 04.08 canon drew, which the mock removes. The heading
   * itself may not go with it: the only other headings on this screen are the top bar's `h1` and
   * each card's `h3`, so without one the outline jumps H1 → H3 and a reader navigating by heading is
   * told the cards belong to a section that does not exist. Hidden, exactly as `WeekPane` hides its
   * twin (`planBuilder.week`).
   */
  readonly title: string;
  /** The "Wzór powtarzalnego cyklu" row, shared in shape with the week's — see `PatternBarView`. */
  readonly pattern: PatternBarView;
  readonly columns: readonly CycleColumnView[];
  readonly add: TapeAddView;
  readonly loop: CycleLoopView;
  /** "Ten cykl nie ma jeszcze żadnego treningu." — shown instead of the row when it is empty. */
  readonly emptyLabel: string;
}

export interface TapeViewModelInput {
  readonly draft: PlanDraft;
  readonly projection: TapeProjection;
  readonly phases: readonly PhaseSummary[];
  readonly load: MuscleLoadBreakdown;
  readonly lookUpExercise: LookUpExercise;
  /** Whether that catalogue read is still open — the volume rail's only wait. See `volumeStatus`. */
  readonly isAttributionPending: boolean;
  readonly t: Translate;
  readonly locale: string;
}

function toRestColumn(
  t: Translate,
  daySlotToken: string,
  days: number,
  sessionName: string,
): RestColumnView {
  // Only the range. A running fork does not lock the gap (owner, 15 September 2026): it spaces the
  // upcoming trainings and dates nothing (R2, R18).
  const decrease = days <= REST_DAYS_AFTER_RANGE.min ? t(`${KEY}.tape.restAtMinimum`) : null;
  const increase = days >= REST_DAYS_AFTER_RANGE.max ? t(`${KEY}.tape.restAtMaximum`) : null;
  return {
    key: `rest-${daySlotToken}`,
    daySlotToken,
    label: t(`${KEY}.tape.restLabel`),
    value: days === 0 ? t(`${KEY}.tape.restNone`) : t(`${KEY}.tape.restValue`, { count: days }),
    // A SEPARATELY NAMED key at zero, not a plural form. Polish routes `count === 0` to `_many`, so
    // a counted sentence there would have to read "0 dni" — a figure the tile beside it deliberately
    // does not print.
    description:
      days === 0
        ? t(`${KEY}.tape.restDescriptionNone`, { name: sessionName })
        : t(`${KEY}.tape.restDescription`, { count: days, name: sessionName }),
    isZero: days === 0,
    reason: decrease ?? increase,
    decrease: {
      value: days - 1,
      label: t(`${KEY}.tape.restDecrease`, { name: sessionName }),
      disabledReason: decrease,
    },
    increase: {
      value: days + 1,
      label: t(`${KEY}.tape.restIncrease`, { name: sessionName }),
      disabledReason: increase,
    },
  };
}

export function toTapeViewModel({
  draft,
  projection,
  phases,
  load,
  lookUpExercise,
  isAttributionPending,
  t,
  locale,
}: TapeViewModelInput): TapeViewModel {
  const total = projection.days.length;
  // Every training's name by position, read once and handed to each card, because a card's move
  // menu has to name what already stands where it would land and cannot see its siblings.
  const occupants = projection.days.map((entry) =>
    dayDisplayName(t, entry.card.title, entry.position),
  );

  // The numbering and the length, from the one module that sums `restDaysAfter`. The slots come back
  // in the order the canvas draws them — day, gap, day, gap — so they are walked with one cursor
  // rather than recounted here.
  const numbering = cycleDayNumbering(projection.days);

  const columns: CycleColumnView[] = [];
  const dayTargets: DayMoveTargetView[] = [];
  let slot = 0;

  // `cycleDayNumbering` walks the SAME list this loop walks and emits a day slot then a gap slot for
  // every entry, so `slot` and `index` cannot come apart: the pair below is `[day, rest]` for this
  // entry by construction, and the defaults on the two reads are unreachable rather than defensive.
  // They stay narrow on purpose — a wider fallback here is what the gap's own `?? 0` was, and that
  // one could have written a real gap back to zero.
  projection.days.forEach((entry, index) => {
    const daySlot = numbering.slots[slot];
    const restSlot = numbering.slots[slot + 1];
    slot += 2;
    const name = occupants[index] ?? '';

    columns.push({
      kind: 'day',
      key: entry.card.slotToken,
      // Where the training falls in the CYCLE, which after a break is not its position among the
      // trainings — "Dzień 3" can be the second training. Both facts stay reachable and neither is
      // hidden: the head says where the day falls, and the move machinery says "Pozycja 2 z 3".
      head: t(`${KEY}.tape.dayHead`, { number: daySlot?.kind === 'day' ? daySlot.dayNumber : 1 }),
      // The mock's "~64 min" over the card. Nothing at all when the trainer authored no estimate —
      // never "~0 min" — which is also what the week's column head does with the same field.
      sub: entry.card.durationMinutes === null
        ? null
        : t(`${KEY}.dayDuration`, { minutes: entry.card.durationMinutes }),
      day: toDayCardView(
        t,
        locale,
        entry.card,
        load.days[index],
        lookUpExercise,
        {
          index,
          position: entry.position,
          menuGroupLabel: t(`${KEY}.dnd.moveToPosition`),
          liftedLabel: (sessionName) =>
            t(`${KEY}.dnd.dayLiftedPosition`, {
              name: sessionName,
              position: entry.position,
              total,
            }),
          targets: positionTargets(t, { occupants, currentIndex: index }),
        },
        isAttributionPending,
      ),
    });
    dayTargets.push({
      slotToken: entry.card.slotToken,
      weekday: null,
      // The move menu addresses a training by its POSITION among the trainings, not by the day of
      // the cycle it falls on — "T3" is the third training, which may stand under "DZIEŃ 5". That is
      // the coordinate the command takes, so it is the one the entry may name, and the owner took
      // the word "dzień" off it on 13 September 2026 so the two cannot be read as one.
      label: t(`${KEY}.dnd.dayTargetPosition`, {
        badge: t(`${KEY}.trainingNumber`, { number: entry.position }),
        name,
      }),
      shortLabel: t(`${KEY}.trainingNumber`, { number: entry.position }),
      blockCount: entry.card.blocks.length,
    });

    // ── EVERY training gets a gap column, including one whose break is zero ────────────────────
    // The tempting answer — "draw no column when there is nothing to draw" — makes the gap
    // UNREACHABLE: a zero-gap day has no + to press and no other surface on this screen writes
    // `restDaysAfter`. The trainer could shorten a gap to nothing and never get it back. So the
    // column is always an element of the row, reading "bez przerwy" when there is no break. Mobile
    // keeps the same field permanently editable in its day settings.
    // The gap comes from the DAY, which is the field the stepper writes and the save sends. It used
    // to be read off the numbering slot with a `?? 0` behind it, and that fallback was a hazard
    // rather than a default: a desynced cursor would have shown a real gap as zero and let the next
    // press of + write 1 over it. The slot is asked only for the two NUMBERS the head prints, which
    // are the one thing the day does not know.
    const restDays = entry.restDaysAfter;
    columns.push({
      kind: 'rest',
      key: `rest-${entry.card.slotToken}`,
      // No head at all at zero: the gap consumes no day number, so there is nothing to print and
      // the mock draws the caption empty.
      head:
        restDays > 0 && restSlot?.kind === 'rest'
          ? t(`${KEY}.tape.restHead`, {
              count: restDays,
              from: restSlot.from,
              to: restSlot.to,
            })
          : null,
      rest: toRestColumn(t, entry.card.slotToken, restDays, name),
    });
  });

  // Written ONCE and interpolated into both places that print it — the pattern row's summary and the
  // volume rail's caption. The mock computes its own `cycLen` once and prints it eighty lines apart;
  // two derivations would drift on the first change to either.
  const cycleLength = t(`${KEY}.cycleLength`, { count: numbering.cycleLengthDays });

  const chrome = toBuilderChrome({
    draft,
    phases,
    phase: projection.phase,
    load: load.week,
    // The PLAN's span — "10 cykli · 3 fazy" — which is the same fact in the same slot the week's
    // band carries, declined onto the cycle by `unitNoun`. It read "5 treningów w cyklu" while the
    // canvas had no other place to state the cycle's shape; the mock's pattern row is that place
    // now, and two rows stating the phase's contents would be one of them repeating the other.
    bandTotal: t(`${KEY}.bandTotal`, {
      cycles: t(`${KEY}.cycles`, {
        count: phases.reduce((sum, each) => sum + each.cycleCount, 0),
      }),
      phases: t(`${KEY}.phases`, { count: phases.length }),
    }),
    dayTargets,
    cycleLengthDays: numbering.cycleLengthDays,
    isAttributionPending,
    t,
    locale,
  });

  return {
    chrome,
    title: t(`${KEY}.cycle`),
    pattern: {
      title: t(`${KEY}.repeatingCycleTitle`),
      // No countable half: a sequence phase's cycle count is set in the PHASE BAND, and the mock
      // draws no stepper in this row. See `PatternBarView.repeat`.
      repeat: null,
      note: t(`${KEY}.repeatCycle.loopPill`),
      summary: t(`${KEY}.repeatCycle.summary`, {
        days: t(`${KEY}.trainingDays`, { count: projection.trainingCount }),
        length: cycleLength,
      }),
    },
    columns,
    add: {
      label: t(`${KEY}.tape.addDay`),
      tileLabel: t(`${KEY}.tape.addDayTile`),
      // The chrome's answer, so the tile and the week's add affordance can never disagree. The tape
      // prints the FULL sentence: its one add tile spans the row and has the room the week's
      // 80px column does not. See `AddDayRefusalView`.
      disabledReason: chrome.addDayRefusal?.full ?? null,
    },
    loop: {
      title: t(`${KEY}.tape.loop`),
      hint: t(`${KEY}.tape.loopReturn`),
      description: t(`${KEY}.tape.loopDescription`),
    },
    emptyLabel: t(`${KEY}.tape.empty`),
  };
}
